import ol from 'openlayers'
import { DataTheme } from 'src/Enums'
import { DataforsyningenToken } from '../dataforsyningen/Dataforsyningen'
import { ContextTag } from '../wiki/Wiki'
import { GeoLayer, AbstractLayer, AbstractLayerOptions, SubLayerDescriptor } from './Layer'
import { layerCopyrights, LayerDescription, LayerGroup, LayerParamsObject, LayerType } from './LayerRegistry'

/**
 * Map layer class for the sea level on land WMS source from Geo.
 */
class SeaLevelOnLandLayer extends AbstractLayer implements GeoLayer {
  private static readonly token: string = DataforsyningenToken
  static readonly defaultParams: LayerParamsObject = {
    opacity: 0.5,
    subLayers: {
      labelId: 'map.layer.sealevel.sublayers',
      key: 'havvandpaaland',
      subLayerDescriptors: [],
      inputEnabled: true,
      maximum: 0,
      minimum: 0,
      step: 0,
      unit: 'm',
      list: [],
      selected: {
        value: 0,
        subLayer: ''
      }
    }
  }

  constructor (options: any) {
    const defaultOptions: AbstractLayerOptions = {
      layerType: LayerType.SEA_LEVEL_ON_LAND,
      enabled: true,
      minZoom: 7,
      zIndex: 2
    }
    super({ ...defaultOptions, ...options })

    // Initialize sublayers.
    options.param = this.initSubLayers(options.params)

    let { subLayers, opacity } = options.params
    opacity = opacity === undefined ? SeaLevelOnLandLayer.defaultParams.opacity : opacity

    const fallbackLayer = subLayers !== undefined && subLayers.list.length > 0 ? subLayers.list[0] : ''
    const subLayer = subLayers !== undefined && subLayers.selected.subLayer === '' ? fallbackLayer : subLayers.selected

    const wmtsOptions = ol.source.WMTS.optionsFromCapabilities(options.capabilities, {
      layer: subLayer.subLayer
    })

    const wmts = new ol.source.WMTS(wmtsOptions)
    wmts.setUrls([`https://api.dataforsyningen.dk/dhm_havvandpaaland?token=${SeaLevelOnLandLayer.token}`])

    this.olLayer = new ol.layer.Tile({
      visible: this.enabled,
      opacity: opacity,
      source: wmts
    })
  }

  getSubLayerName (params: LayerParamsObject, value: number): string {
    if (params.subLayers === undefined) {
      return ''
    }
    return `${params.subLayers.key}_${String(value).replace('.', '_')}`
  }

  initSubLayers (params: LayerParamsObject): LayerParamsObject {
    if (params.subLayers === undefined) {
      return params
    }
    let lastValue = 0
    params.subLayers.minimum = 0
    params.subLayers.maximum = 0
    params.subLayers.subLayerDescriptors = []
    for (const subLayer of params.subLayers.list) {
      // Convert string to float
      const value = parseFloat(subLayer.replace(`${params.subLayers.key}_`, '').replace('_', '.'))
      const descriptor: SubLayerDescriptor = {
        value: value,
        subLayer: subLayer
      }
      params.subLayers.subLayerDescriptors.push(descriptor)
      if (value < params.subLayers.minimum) {
        params.subLayers.minimum = value
      }
      if (value > params.subLayers.maximum) {
        params.subLayers.maximum = value
      }
      if (subLayer === params.subLayers.selected.subLayer) {
        params.subLayers.selected = descriptor
      }
      if (lastValue > 0) {
        params.subLayers.step = Math.round((value - lastValue) * 100) / 100 // round value to 1 decimal
      }
      lastValue = value
    }
    return params
  }

  static describe (): LayerDescription {
    return {
      type: LayerType.SEA_LEVEL_ON_LAND,
      name: 'map.layer.sealevel',
      group: LayerGroup.WATER,
      contextTags: [
        ContextTag.SEA_LEVEL_ON_LAND
      ],
      themes: [DataTheme.CLIMATE, DataTheme.ENVIRONMENT],
      capabilitiesUrl: 'https://api.dataforsyningen.dk/service?request=GetCapabilities&version=1.1.1&servicename=dhm_havvandpaaland&service=WMTS',
      legendEnabled: false,
      opacityEnabled: true,
      layerCopyright: layerCopyrights.Geodatastyrelsen
    }
  }
}

export default SeaLevelOnLandLayer
